import React, { useCallback, useEffect, useState } from 'react'
import { Modal } from 'semantic-ui-react'
import styled from 'styled-components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { SubscriptionService } from 'utility/services/subscription'
import { CONTACT_SALES_URL } from 'utility/subscription-usage'

const Body = styled.div`
    .title {
        font-size: 17px;
        font-weight: 700;
        color: #111827;
        margin: 0 0 4px;
    }
    .sub {
        font-size: 13px;
        color: #6b7280;
        margin: 0 0 16px;
    }
    .grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        @media (max-width: 640px) {
            grid-template-columns: 1fr;
        }
    }
    .card {
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 14px;
        background: #fff;
        text-align: left;
    }
    .card.selected {
        border-color: #635bff;
        background: #f5f4ff;
        box-shadow: 0 0 0 1px #635bff;
    }
    .card h4 {
        margin: 0 0 6px;
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }
    .price {
        font-size: 22px;
        font-weight: 800;
        color: #635bff;
    }
    .per {
        font-size: 11px;
        color: #6b7280;
    }
    .badge {
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 2px 8px;
        border-radius: 999px;
        background: #e0e7ff;
        color: #3730a3;
    }
    ul {
        list-style: none;
        padding: 0;
        margin: 10px 0 12px;
        font-size: 12px;
        color: #374151;
    }
    li {
        padding: 3px 0;
        padding-left: 16px;
        position: relative;
    }
    li::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #10b981;
        font-weight: 700;
    }
    .err {
        margin-top: 12px;
        padding: 10px 12px;
        border-radius: 8px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #991b1b;
        font-size: 13px;
    }
    .loading {
        padding: 24px;
        text-align: center;
        color: #6b7280;
    }
`

const planBlurb = plan => {
    const name = (plan.PlanName || plan.planName || '').toLowerCase()
    if (name.includes('free')) {
        return ['10 team seats', '5 active templates']
    }
    if (name.includes('enterprise')) {
        return ['Unlimited everything', 'SSO / SAML', 'Dedicated CSM']
    }
    return ['Unlimited seats', 'Unlimited templates', 'Priority support']
}

const formatCost = plan => {
    const cost = Number(plan.PlanCost ?? plan.planCost ?? 0)
    if (!cost) return { price: 'Custom', per: 'talk to sales' }
    const name = (plan.PlanName || plan.planName || '').toLowerCase()
    if (name.includes('year')) {
        return { price: `$${cost}`, per: 'per year' }
    }
    if (name.includes('free')) {
        return { price: '$0', per: 'forever' }
    }
    return { price: `$${cost}`, per: 'per month' }
}

/**
 * Mockup Step 2 — Choose plan from GET /ServicePlan.
 * Does not change existing Free-limit panels; only used when Upgrade is opened.
 */
export const PlanChooserModal = ({
    open,
    onClose,
    currentPlanId,
    onChoosePaidPlan,
}) => {
    const [plans, setPlans] = useState([])
    const [stripeClientKey, setStripeClientKey] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [selectedId, setSelectedId] = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const data = await SubscriptionService.getServicePlans()
            const list = Array.isArray(data)
                ? data
                : data?.Items || data?.items || data?.Plans || data?.plans || []
            // Mockup Step 4: StripeClientKey from /ServicePlan config
            const keyFromRoot =
                data?.StripeClientKey ||
                data?.stripeClientKey ||
                data?.StripePublishableKey ||
                data?.PublishableKey ||
                data?.Config?.StripeClientKey ||
                data?.config?.StripeClientKey ||
                ''
            const keyFromPlan = list.find(
                p =>
                    p?.StripeClientKey ||
                    p?.stripeClientKey ||
                    p?.StripePublishableKey ||
                    p?.PublishableKey,
            )
            setStripeClientKey(
                keyFromRoot ||
                    keyFromPlan?.StripeClientKey ||
                    keyFromPlan?.stripeClientKey ||
                    keyFromPlan?.StripePublishableKey ||
                    keyFromPlan?.PublishableKey ||
                    '',
            )
            setPlans(list)
            const firstBuyable = list.find(
                p => p.BuyAvailable ?? p.buyAvailable,
            )
            setSelectedId(
                firstBuyable?.PlanId ?? firstBuyable?.planId ?? null,
            )
        } catch (err) {
            const apiErr = err?.error
            const fromList =
                apiErr?.Errors?.[0]?.ErrorMessage ||
                apiErr?.errors?.[0]?.ErrorMessage
            setError(
                fromList ||
                    apiErr?.message ||
                    apiErr?.Message ||
                    err?.message ||
                    'Could not load plans. Please try again.',
            )
            setPlans([])
            setStripeClientKey('')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (open) load()
    }, [open, load])

    const handleChoose = plan => {
        const buyable = plan.BuyAvailable ?? plan.buyAvailable
        const name = (plan.PlanName || plan.planName || '').toLowerCase()
        if (!buyable) {
            if (name.includes('enterprise')) {
                window.location.assign(CONTACT_SALES_URL)
            }
            return
        }
        // Attach ServicePlan StripeClientKey for Stripe.js init (mockup Step 4)
        onChoosePaidPlan?.({
            ...plan,
            StripeClientKey:
                plan.StripeClientKey ||
                plan.stripeClientKey ||
                stripeClientKey ||
                undefined,
        })
    }

    const renderPlanAction = plan => {
        const buyable = plan.BuyAvailable ?? plan.buyAvailable
        const name = plan.PlanName || plan.planName
        if (buyable) {
            return (
                <AppPrimaryButton
                    type="button"
                    onClick={e => {
                        e.stopPropagation()
                        handleChoose(plan)
                    }}
                >
                    Choose {name}
                </AppPrimaryButton>
            )
        }
        if (name?.toLowerCase().includes('enterprise')) {
            return (
                <AppSecondaryButton
                    type="button"
                    onClick={e => {
                        e.stopPropagation()
                        handleChoose(plan)
                    }}
                >
                    Contact sales
                </AppSecondaryButton>
            )
        }
        return null
    }

    return (
        <Modal open={open} size="small" onClose={onClose}>
            <Modal.Content>
                <Body>
                    <div className="title">Choose your plan</div>
                    <p className="sub">Compare and pick what suits you</p>
                    {loading && <div className="loading">Loading plans…</div>}
                    {!loading && (
                        <div className="grid">
                            {plans.map(plan => {
                                const id = plan.PlanId ?? plan.planId
                                const name = plan.PlanName || plan.planName
                                const isCurrent =
                                    currentPlanId != null &&
                                    Number(currentPlanId) === Number(id)
                                const { price, per } = formatCost(plan)
                                const selected = Number(selectedId) === Number(id)
                                return (
                                    <div
                                        key={id}
                                        className={`card${
                                            selected ? ' selected' : ''
                                        }`}
                                        onClick={() => setSelectedId(id)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                setSelectedId(id)
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <h4>
                                            {name}
                                            {isCurrent && (
                                                <span className="badge">
                                                    Current
                                                </span>
                                            )}
                                        </h4>
                                        <div className="price">{price}</div>
                                        <div className="per">{per}</div>
                                        <ul>
                                            {planBlurb(plan).map(line => (
                                                <li key={line}>{line}</li>
                                            ))}
                                        </ul>
                                        {renderPlanAction(plan)}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                    {error && <div className="err">{error}</div>}
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <AppSecondaryButton type="button" onClick={onClose}>
                            Close
                        </AppSecondaryButton>
                    </div>
                </Body>
            </Modal.Content>
        </Modal>
    )
}
